import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpCircle,
  AlertTriangle,
  Banknote,
  CheckCircle,
  Database,
  Download,
  ExternalLink,
  Globe,
  MapPin,
  Pause,
  Pencil,
  RefreshCw,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Money } from '../../components/Money';
import { Tabs } from '../../components/Tabs';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { PosRoleSelector } from '../../components/PosRoleSelector';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { BusinessUsersTable } from '../../components/BusinessUsersTable';
import { toMajor, toMinor } from '../../contracts/money';
import { useAdminBusinesses, useAdminBusiness, useAdminAllPackages, useAdminAllPrices, setBusinessStatus, refundPayment, changeBusinessPlan, createBillableUpgradeInvoice, voidOpenInvoice, createAdminBusinessUser, updateAdminBusinessInfo, useBusinessExtraction, requestBusinessExtraction, approveBusinessExtraction, declineBusinessExtraction, type AdminBusinessDetail, type InviteResult, type CreateUserResult, type ExtractionState } from '../../api/admin';
import { useAdminBusinessLedger } from '../../api/billing';
import { openBusinessPosAsAdmin } from '../../api/pos';
import { formatDate, formatDateOnly, formatInt, timeAgo, dateStatus, slotOn, dhakaToday, EXTRACTION_SLOT_TIME } from '../../lib/format';
import { cn } from '../../lib/cn';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success', trial: 'info', past_due: 'warning', suspended: 'error',
  canceled: 'neutral', pending_payment: 'warning', provisioning: 'info', deleted: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', trial: 'Trial', past_due: 'Past due', suspended: 'Suspended',
  canceled: 'Canceled', pending_payment: 'Pending pay', provisioning: 'Provisioning', deleted: 'Deleted',
};
const DB_LABEL: Record<string, string> = { managed: 'Managed DB', self_hosted: 'Self-hosted DB', legacy: 'Legacy (shared)' };

/** Business detail — header + 7 tabs (Overview, Subscription, Payments, Database, Domains, Users, Audit). */
export function BusinessDetail() {
  const { data: adminBusinesses = [] } = useAdminBusinesses();
  // Select the routed business (the page used to always show adminBusinesses[0]).
  const params = useParams({ strict: false }) as { id?: string };
  const b = adminBusinesses.find((x) => x.id === params.id);
  // Full drill-in (real DB/subscription/payments) — the list only carries the header fields.
  const { data: detail } = useAdminBusiness(params.id ?? '');
  const { data: ledger } = useAdminBusinessLedger(params.id ?? '');
  const qc = useQueryClient();
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [posBusy, setPosBusy] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [upgradeInvoiceOpen, setUpgradeInvoiceOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function openPosAsAdmin() {
    if (!b || posBusy) return;
    setPosBusy(true);
    try {
      await openBusinessPosAsAdmin(b.id, '/home');
    } finally {
      setPosBusy(false);
    }
  }

  async function setStatus(status: Parameters<typeof setBusinessStatus>[1]) {
    const id = params.id;
    if (!id || statusBusy) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await setBusinessStatus(id, status);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
    } catch (e) {
      // Previously swallowed — a failed Suspend/Activate looked like a no-op. Surface it.
      setStatusError(e instanceof Error ? e.message : 'Could not change status. Please try again.');
    } finally {
      setStatusBusy(false);
    }
  }

  // ── Refund a payment (claws back reseller commission) ──────────────────────
  const refundablePayments = (detail?.payments ?? []).filter((p) => p.status === 'paid');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState(''); // major units (৳)
  const [refundReason, setRefundReason] = useState('');
  const [refundChannel, setRefundChannel] = useState<'refund' | 'chargeback'>('refund');
  const [refundError, setRefundError] = useState<string | null>(null);
  const refundTarget = refundablePayments.find((p) => p.id === refundPaymentId);

  function openRefund() {
    const target = refundablePayments[0];
    if (!target) return;
    setRefundPaymentId(target.id);
    setRefundAmount(String(toMajor(target.amountMinor)));
    setRefundReason('');
    setRefundChannel('refund');
    setRefundError(null);
    setRefundOpen(true);
  }

  async function doRefund() {
    const id = params.id;
    const amountMinor = toMinor(Number(refundAmount));
    if (!refundPaymentId || !Number.isFinite(amountMinor) || amountMinor <= 0) {
      setRefundError('Enter a valid amount.');
      return;
    }
    setRefundBusy(true);
    setRefundError(null);
    try {
      await refundPayment(refundPaymentId, {
        amountMinor,
        reason: refundReason.trim() || undefined,
        channel: refundChannel,
      });
      if (id) void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      setRefundOpen(false);
    } catch (e) {
      setRefundError(e instanceof Error ? e.message : 'Refund failed.');
    } finally {
      setRefundBusy(false);
    }
  }

  if (adminBusinesses.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }
  if (!b) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Business not found.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/businesses"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All businesses
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-12 h-12 rounded-lg bg-zinc-950 dark:bg-zinc-50 grid place-items-center text-base font-semibold text-white dark:text-zinc-950 flex-shrink-0">
              {b.name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('')}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 truncate">
                  {b.name}
                </h1>
                <Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{STATUS_LABEL[b.status] ?? b.status}</Badge>
                {b.paymentReviewAt && <Badge tone="warning">Review</Badge>}
                <span className="text-xs text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">{b.plan} · {DB_LABEL[b.dbMode] ?? b.dbMode}</span>
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                Reseller: {b.resellerName ?? 'Direct'} · Owner: {b.ownerEmail} · <span className="mono">business_id: {b.id.replace('biz_', '')}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" size="sm" iconLeft={<ExternalLink className="w-4 h-4" />} loading={posBusy} onClick={openPosAsAdmin}>
              Open POS
            </Button>
            <Button size="sm" iconLeft={<Banknote className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record payment
            </Button>
            {b.status === 'suspended' ? (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('active')}>
                Activate
              </Button>
            ) : (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('suspended')}>
                Suspend
              </Button>
            )}
            <Button variant="secondary" size="sm" iconLeft={<XCircle className="w-4 h-4" />} disabled={statusBusy || b.status === 'canceled'} onClick={() => setStatus('canceled')}>
              Cancel
            </Button>
            {/* A greyed button whose only explanation is a `title` tooltip is silent on any
                touch device — which is where this is most often read. Show the reason as
                text instead of rendering a dead control. */}
            {refundablePayments.length === 0 ? (
              <span className="text-xs text-zinc-500 dark:text-zinc-400 self-center">
                Refund unavailable — no paid payments yet
              </span>
            ) : (
              <Button variant="secondary" size="sm" onClick={openRefund}>
                Refund
              </Button>
            )}
            <Button variant="secondary" size="sm" iconLeft={<Pencil className="w-4 h-4" />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </div>
        </div>
        {statusError && (
          <p className="mt-3 text-sm text-rose-600 dark:text-rose-400 lg:text-right">{statusError}</p>
        )}
      </Card>

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: <OverviewTab detail={detail} onChangePlan={() => setPlanOpen(true)} onUpgradeInvoice={() => setUpgradeInvoiceOpen(true)} /> },
            { id: 'subscription', label: 'Subscription', content: <SubscriptionTab sub={detail?.subscription ?? null} audit={detail?.audit ?? []} onChangePlan={() => setPlanOpen(true)} onUpgradeInvoice={() => setUpgradeInvoiceOpen(true)} /> },
            // Badge counts PAID payments only — matching the tab's default "Paid" filter.
            // Pending/failed attempts still show under its "All" filter, they just don't
            // inflate the count ("Payments 2" with zero real money reads as a bug).
            { id: 'payments', label: 'Payments', count: detail?.payments?.filter((p) => p.status === 'paid').length, content: <PaymentsTab payments={detail?.payments ?? []} /> },
            { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
            { id: 'database', label: 'Database', content: <DatabaseTab db={detail?.database ?? null} dbMode={detail?.dbMode} /> },
            { id: 'extraction', label: 'Extraction', content: <ExtractionTab businessId={params.id ?? ''} businessName={b.name} dbMode={detail?.dbMode} /> },
            { id: 'domains', label: 'Domains', count: detail?.domains?.length, content: <DomainsTab domains={detail?.domains ?? []} /> },
            { id: 'users', label: 'Users', count: detail?.users?.length, content: <UsersTab businessId={params.id ?? ''} onChanged={() => { if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] }); }} /> },
            { id: 'audit', label: 'Audit', count: detail?.audit?.length, content: <AuditTab audit={detail?.audit ?? []} /> },
            { id: 'locations', label: 'Locations', count: detail?.locations?.length, content: <LocationsTab locations={detail?.locations ?? []} /> },
          ]}
        />
      </Card>

      <EditBusinessModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        businessId={b.id}
        initialName={b.name}
        initialOwnerEmail={detail?.ownerEmail ?? b.ownerEmail ?? ''}
        onChanged={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
        }}
      />

      <ChangePlanModal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        businessId={b.id}
        businessName={b.name}
        sub={detail?.subscription ?? null}
        isDirect={!detail?.resellerId}
        onChanged={() => {
          if (params.id) {
            void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
            void qc.invalidateQueries({ queryKey: ['admin', 'ledger', params.id] });
          }
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
        }}
      />

      <UpgradeInvoiceModal
        open={upgradeInvoiceOpen}
        onClose={() => setUpgradeInvoiceOpen(false)}
        businessId={b.id}
        businessName={b.name}
        sub={detail?.subscription ?? null}
        isDirect={!detail?.resellerId}
        onChanged={() => {
          if (params.id) {
            void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
            void qc.invalidateQueries({ queryKey: ['admin', 'ledger', params.id] });
          }
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
        }}
      />

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={b.id}
        businessName={b.name}
        onRecorded={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
        }}
      />

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Issue refund"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={refundBusy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={doRefund} loading={refundBusy}>
              {refundChannel === 'chargeback' ? 'Record chargeback' : 'Issue refund'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {refundTarget && (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Refunding{' '}
              <span className="font-medium text-zinc-900 dark:text-zinc-100 mono">{refundTarget.invoiceNo}</span>{' '}
              (<Money minor={refundTarget.amountMinor} hideCents tone="neutral" /> · {refundTarget.method}). This claws
              back the reseller commission for this payment.
            </p>
          )}
          <Input label="Amount (৳)" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          <Input
            label="Reason (optional)"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="e.g. duplicate charge"
          />
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Channel</span>
            <div className="flex gap-2">
              {(['refund', 'chargeback'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setRefundChannel(c)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition capitalize ${
                    refundChannel === c
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {refundError && <p className="text-sm text-rose-600 dark:text-rose-400">{refundError}</p>}
        </div>
      </Modal>
    </div>
  );
}

function OverviewTab({ detail, onChangePlan, onUpgradeInvoice }: { detail: AdminBusinessDetail | undefined; onChangePlan: () => void; onUpgradeInvoice: () => void }) {
  const sub = detail?.subscription;
  const db = detail?.database;
  const audit = detail?.audit ?? [];
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  const dun = sub?.dunning ?? null;
  return (
    <div className="space-y-6">
      {/* An admin used to get a bare "Past due" badge and no way to answer the only question a
          customer ever asks — "when do I actually lose access?" — without querying the database
          by hand. Every date below comes from the server's dunning ladder. */}
      {dun && (
        <Banner
          tone={dun.suspended ? 'error' : dun.daysUntilSuspend <= 1 ? 'warning' : 'info'}
          title={
            dun.suspended
              ? `Suspension due — invoice ${dun.daysOverdue} days overdue`
              : `Unpaid invoice — POS access suspends on ${formatDateOnly(dun.suspendsOn)}`
          }
        >
          Invoice <strong>{dun.invoiceNo}</strong> for <strong>{taka(dun.amountDueMinor)}</strong> was
          due on <strong>{formatDateOnly(dun.dueAt)}</strong> and is{' '}
          <strong>{dun.daysOverdue} day{dun.daysOverdue === 1 ? '' : 's'} overdue</strong>.{' '}
          {dun.suspended ? (
            <>POS access is due to be suspended — the nightly billing run at 02:30 Asia/Dhaka applies it.</>
          ) : (
            <>
              Access continues until <strong>{formatDateOnly(dun.suspendsOn)}</strong>
              {dun.daysUntilSuspend === 1 ? ' (tomorrow)' : ` (${dun.daysUntilSuspend} days away)`}, when
              the nightly billing run at 02:30 Asia/Dhaka suspends it if the invoice is still unpaid.
            </>
          )}{' '}
          If it stays unpaid, the subscription is canceled on{' '}
          <strong>{formatDateOnly(dun.cancelsOn)}</strong>. Recording a payment clears all of this
          immediately.
        </Banner>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader
            title="Subscription"
            action={sub ? (
              <div className="flex items-center gap-2">
                <Button size="sm" iconLeft={<ArrowUpCircle className="w-3.5 h-3.5" />} onClick={onUpgradeInvoice}>Upgrade &amp; invoice</Button>
                <Button variant="secondary" size="sm" iconLeft={<ArrowLeftRight className="w-3.5 h-3.5" />} onClick={onChangePlan}>No-charge override</Button>
              </div>
            ) : undefined}
          />
          {sub ? (
            <>
              <dl className="space-y-2.5 text-sm">
                <Row k="Plan" v={`${sub.plan ?? '—'} · ${taka(sub.unitAmountMinor)}/${sub.interval}`} />
                <Row k="Status" v={<Badge tone={sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error'}>{sub.status}</Badge>} />
                <Row k="Started" v={<span className="tabular-nums">{sub.startedAt ? formatDate(sub.startedAt) : '—'}</span>} />
                <Row
                  k={sub.status === 'trial' ? 'Trial ends' : 'Renews'}
                  v={
                    <span className="tabular-nums">
                      {(sub.status === 'trial' ? sub.trialEndsAt ?? sub.currentPeriodEnd : sub.currentPeriodEnd)
                        ? formatDate((sub.status === 'trial' ? sub.trialEndsAt ?? sub.currentPeriodEnd : sub.currentPeriodEnd)!)
                        : '—'}
                    </span>
                  }
                />
              </dl>
              <SubscriptionNotes sub={sub} audit={audit} />
            </>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record.</p>
          )}
        </Card>

        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Database" />
          {db ? (
            <dl className="space-y-2.5 text-sm">
              <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted' : 'Managed'} />
              <Row k="Status" v={db.status} />
              <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
              <Row k="DB name" v={<span className="mono text-xs">{db.dbName}</span>} />
            </dl>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">Shared legacy database (no dedicated DB).</p>
          )}
        </Card>
      </div>

      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Recent activity" />
        {audit.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No recent activity recorded.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {audit.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-20 flex-shrink-0 pt-0.5">{a.occurredAt ? formatDate(a.occurredAt) : '—'}</span>
                <span className="text-zinc-700 dark:text-zinc-200"><span className="mono text-xs">{a.action}</span> · {a.category}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SubscriptionTab({ sub, audit, onChangePlan, onUpgradeInvoice }: { sub: AdminBusinessDetail['subscription']; audit: AdminBusinessDetail['audit']; onChangePlan: () => void; onUpgradeInvoice: () => void }) {
  if (!sub) {
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Subscription" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record for this business.</p>
      </Card>
    );
  }
  const tone =
    sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error';
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  const isTrial = sub.status === 'trial';
  const dateLabel = isTrial ? 'Trial ends' : 'Renews';
  const dateValue = isTrial ? sub.trialEndsAt ?? sub.currentPeriodEnd : sub.currentPeriodEnd;
  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Current plan" subtitle={`${sub.plan ?? '—'} · ${sub.interval}`} action={<Badge tone={tone}>{sub.status}</Badge>} />
        <div className="grid grid-cols-4 gap-4">
          <Stat label="Price" v={taka(sub.unitAmountMinor)} />
          <Stat label="Interval" v={sub.interval} />
          <Stat label="Started" v={sub.startedAt ? formatDate(sub.startedAt) : '—'} />
          <Stat label={dateLabel} v={dateValue ? formatDate(dateValue) : '—'} />
        </div>
        {sub.usage && (
          <div className="mt-5 pt-4 border-t border-zinc-200 dark:border-zinc-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <UsageBar label="Locations" used={sub.usage.locations.used} limit={sub.usage.locations.limit} />
              <UsageBar label="Users" used={sub.usage.users.used} limit={sub.usage.users.limit} />
              <UsageBar label="Products" used={sub.usage.products.used} limit={sub.usage.products.limit} compact />
            </div>
            <PlanLimitNote usage={sub.usage} entitlements={sub.entitlements} interval={sub.interval} />
          </div>
        )}
        <SubscriptionNotes sub={sub} audit={audit} />
        <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 flex items-center gap-2">
          <Button size="sm" iconLeft={<ArrowUpCircle className="w-4 h-4" />} onClick={onUpgradeInvoice}>
            Upgrade &amp; invoice
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<ArrowLeftRight className="w-4 h-4" />} onClick={onChangePlan}>
            No-charge override
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** Usage vs the plan's cap. limit 0 = unlimited/unset → "∞" and no bar fill. Mirrors the
 *  owner-facing bar on /subscription so both roles read the same numbers. */
function UsageBar({ label, used, limit, compact = false }: { label: string; used: number; limit: number; compact?: boolean }) {
  const unlimited = !limit || limit <= 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const over = !unlimited && used >= limit;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className={cn('text-xs tabular-nums', over ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-zinc-500 dark:text-zinc-400')}>
          {unlimited ? `${compact ? formatInt(used) : used} / ∞` : compact ? `${formatInt(used)} / ${formatInt(limit)}` : `${used} / ${limit}`}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            pct > 90 ? 'bg-rose-500' : pct > 75 ? 'bg-amber-500' : 'bg-brand dark:bg-brand-dm',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Says out loud what a full meter MEANS. Both planes refuse the next add once a cap is
 *  reached (the SaaS with a 409, the POS with "Maximum … limit reached"), and an admin
 *  previously had no way to see that coming — the invoice cap has no meter at all.
 *  "Over" is a real state, not a bug: a business can carry more users than its CURRENT
 *  plan allows if it renewed onto a smaller one (or the package's caps were cut). */
function PlanLimitNote({ usage, entitlements, interval }: {
  usage: NonNullable<AdminBusinessDetail['subscription']>['usage'];
  entitlements: NonNullable<AdminBusinessDetail['subscription']>['entitlements'];
  interval: string;
}) {
  const metered = !usage ? [] : ([
    ['locations', usage.locations], ['users', usage.users], ['products', usage.products],
  ] as const).filter(([, u]) => u.limit > 0 && u.used >= u.limit);
  const over = metered.filter(([, u]) => u.used > u.limit).map(([k]) => k);
  const at = metered.filter(([, u]) => u.used === u.limit).map(([k]) => k);
  // Invoice caps are per-interval (a plan can allow N/month and M/year), so read the
  // one matching THIS subscription's billing interval — condInvoicesYearly falls back
  // to the monthly figure when no yearly key exists, which would otherwise mislabel it.
  const yearly = interval === 'year';
  const invoiceCap = (yearly ? entitlements?.maxInvoicesYearly ?? entitlements?.maxInvoices : entitlements?.maxInvoices) ?? null;
  if (metered.length === 0 && invoiceCap == null) return null;
  const list = (xs: string[]) => xs.join(' and ');
  return (
    <div className="mt-3 space-y-1.5 text-xs">
      {over.length > 0 && (
        <p className="text-rose-600 dark:text-rose-400">
          Over the plan limit for {list(over)} — already above what this plan allows, so adding another is refused
          (in the POS too) until the plan changes.
        </p>
      )}
      {at.length > 0 && (
        <p className="text-amber-600 dark:text-amber-400">
          At the plan limit for {list(at)} — adding another will be refused until the plan changes.
        </p>
      )}
      {invoiceCap != null && (
        <p className="text-zinc-500 dark:text-zinc-400">
          Invoices: {formatInt(invoiceCap)} per {yearly ? 'year' : 'month'} (no live count mirrored).
        </p>
      )}
    </div>
  );
}

/** Shared by OverviewTab + SubscriptionTab: explains what happens at the trial/renewal
 *  date (which plan+price it bills, since there's no auto-charge — SSLCommerz requires
 *  the owner to manually pay), and — when the plan was recently changed — that the date
 *  shown didn't move with it. Renders nothing when neither applies. */
function SubscriptionNotes({ sub, audit }: { sub: NonNullable<AdminBusinessDetail['subscription']>; audit: AdminBusinessDetail['audit'] }) {
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  const change = findLastPlanChange(audit);
  if (sub.status !== 'trial' && !change) return null;
  return (
    <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700 space-y-1.5 text-xs text-zinc-500 dark:text-zinc-400">
      {sub.status === 'trial' && (
        <p>
          Then bills {taka(sub.unitAmountMinor)}/{sub.interval} ({sub.plan ?? 'current plan'}) — no card on file to
          auto-charge; the owner must pay manually via SSLCommerz.
        </p>
      )}
      {change && (
        <p>
          Plan changed {change.occurredAt ? formatDate(change.occurredAt) : '—'} by {change.actorEmail}: {change.fromPackage} → {change.toPackage} (date above unchanged by this change).
        </p>
      )}
    </div>
  );
}

/** Admin plan override: pick a published package + interval. Entitlements switch now;
 *  the new price bills from the next renewal (no proration, renewal date unchanged). */
function ChangePlanModal({ open, onClose, businessId, businessName, sub, onChanged, isDirect }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  sub: AdminBusinessDetail['subscription'];
  onChanged: () => void;
  /** Direct (reseller-less) business → only platform-catalog packages are pickable. */
  isDirect: boolean;
}) {
  const { data: allPackages = [] } = useAdminAllPackages();
  const { data: allPrices = [] } = useAdminAllPrices();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [packageId, setPackageId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerVoid, setOfferVoid] = useState(false);
  const [done, setDone] = useState<{ plan: string; amountMinor: number } | null>(null);
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');

  const priceFor = (pkgId: string, iv: 'month' | 'year') =>
    allPrices.find((p) => p.packageId === pkgId && p.interval === iv && p.status === 'active');
  // Published platform/reseller packages that actually sell in the chosen interval.
  const choices = allPackages.filter((p) => p.status === 'published' && priceFor(p.id, interval) && (!isDirect || p.soldBy === 'all'));

  function close() {
    setPackageId(''); setInterval('month'); setReason(''); setBusy(false); setError(null); setOfferVoid(false); setDone(null);
    onClose();
  }
  async function submit() {
    if (!packageId) { setError('Pick a plan first.'); return; }
    if (!reason.trim()) { setError('A reason is required for a no-charge override.'); return; }
    setBusy(true); setError(null); setOfferVoid(false);
    try {
      const res = await changeBusinessPlan(businessId, { packageId, interval, reason: reason.trim() });
      setDone({ plan: res.plan, amountMinor: res.amountMinor });
      onChanged();
    } catch (e) {
      const message = e instanceof Error && e.message ? e.message : 'Could not change the plan.';
      setError(message);
      // The open-invoice guard: offer the companion action right here.
      if (message.includes('unpaid invoice')) setOfferVoid(true);
    } finally {
      setBusy(false);
    }
  }
  /** Void the open renewal invoice, then retry the plan change (the cron reissues at the new price). */
  async function voidAndRetry() {
    if (!reason.trim()) { setError('A reason is required for a no-charge override.'); return; }
    setBusy(true); setError(null);
    try {
      await voidOpenInvoice(businessId);
      setOfferVoid(false);
      onChanged();
      const res = await changeBusinessPlan(businessId, { packageId, interval, reason: reason.trim() });
      setDone({ plan: res.plan, amountMinor: res.amountMinor });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not void the invoice.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title={done ? 'Plan changed' : 'No-charge override'}
      size="md"
      footer={done ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!packageId || !reason.trim() || busy} loading={busy}>Apply no-charge override</Button>
        </>
      )}
    >
      {done ? (
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          <span className="font-medium">{businessName}</span> is now on <span className="font-medium">{done.plan}</span>{' '}
          ({taka(done.amountMinor)}/{interval}). The new price bills from the next renewal.
        </p>
      ) : (
        <div className="space-y-4">
          {sub && (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Current: <span className="font-medium text-zinc-900 dark:text-zinc-100">{sub.plan ?? '—'}</span> · {taka(sub.unitAmountMinor)}/{sub.interval}
            </p>
          )}
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Billing interval</span>
            <div className="flex gap-2">
              {(['month', 'year'] as const).map((iv) => (
                <button
                  key={iv}
                  type="button"
                  onClick={() => { setInterval(iv); setPackageId(''); }}
                  className={`px-3 py-1.5 rounded-md text-sm border transition ${
                    interval === iv
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {iv === 'month' ? 'Monthly' : 'Yearly'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">New plan</span>
            {choices.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">No published packages with an active {interval === 'year' ? 'yearly' : 'monthly'} price.</p>
            ) : (
              <div className="space-y-2">
                {choices.map((p) => {
                  const price = priceFor(p.id, interval)!;
                  const isCurrent = sub?.plan === p.name && sub?.interval === interval;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPackageId(p.id)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border text-left transition ${
                        packageId === p.id
                          ? 'border-brand bg-brand-subtle dark:bg-brand/15 dark:border-brand-dm'
                          : 'border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-zinc-950 dark:text-zinc-50">
                          {p.name}
                          {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Current</span>}
                        </span>
                        {p.tagline && <span className="block text-xs text-zinc-500 dark:text-zinc-400 truncate">{p.tagline}</span>}
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-zinc-950 dark:text-zinc-50 flex-shrink-0">
                        {taka(price.amount)}<span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">/{interval}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Reason (required — this does not bill the customer)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. goodwill comp, data-entry correction, migration adjustment…"
              rows={2}
              className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition resize-none"
            />
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Limits and modules update immediately. The renewal date stays the same; the new price is billed from the next renewal — no proration.
          </p>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
          {offerVoid && (
            <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 space-y-2">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                The due invoice was priced at the old plan. Voiding it cancels that bill — the next
                billing run reissues a fresh invoice at the NEW plan's price. (To collect the old
                bill instead, use Record payment first, then change the plan.)
              </p>
              <Button variant="destructive" size="sm" onClick={voidAndRetry} disabled={!reason.trim() || busy} loading={busy}>
                Void the due invoice & change plan
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Admin billable upgrade: creates an OPEN invoice at the new price. The plan does
 *  NOT change until the invoice is paid — by the owner (existing Billing page pay
 *  flow) or by admin recording cash against it. */
function UpgradeInvoiceModal({ open, onClose, businessId, businessName, sub, onChanged, isDirect }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  sub: AdminBusinessDetail['subscription'];
  onChanged: () => void;
  /** Direct (reseller-less) business → only platform-catalog packages are pickable. */
  isDirect: boolean;
}) {
  const { data: allPackages = [] } = useAdminAllPackages();
  const { data: allPrices = [] } = useAdminAllPrices();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [packageId, setPackageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ invoiceNo: string; amountMinor: number } | null>(null);
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');

  const priceFor = (pkgId: string, iv: 'month' | 'year') => allPrices.find((p) => p.packageId === pkgId && p.interval === iv && p.status === 'active');
  const choices = allPackages.filter((p) => p.status === 'published' && priceFor(p.id, interval) && (!isDirect || p.soldBy === 'all'));

  function close() {
    setPackageId(''); setInterval('month'); setBusy(false); setError(null); setDone(null);
    onClose();
  }
  async function submit() {
    if (!packageId) { setError('Pick a plan first.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await createBillableUpgradeInvoice(businessId, { packageId, interval });
      setDone({ invoiceNo: res.invoiceNo, amountMinor: res.amountMinor });
      onChanged();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Could not create the upgrade invoice.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title={done ? 'Invoice created' : 'Upgrade & invoice'}
      size="md"
      footer={done ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!packageId || busy} loading={busy}>Create invoice</Button>
        </>
      )}
    >
      {done ? (
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Invoice <span className="font-medium mono">{done.invoiceNo}</span> for{' '}
          <span className="font-medium">{taka(done.amountMinor)}</span> is now open on{' '}
          <span className="font-medium">{businessName}</span>'s account. The plan changes once it's paid —
          the owner can pay it from their Billing page, or you can record cash against it.
        </p>
      ) : (
        <>
          {error && <div className="mb-3 text-sm text-rose-600 dark:text-rose-400">{error}</div>}
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
            Current plan: <span className="font-medium">{sub?.plan ?? '—'}</span> · {sub ? taka(sub.unitAmountMinor) : '—'}/{sub?.interval}.
            This creates an open invoice — the plan will not change until it's paid.
          </p>
          <div className="flex gap-2 mb-3">
            {(['month', 'year'] as const).map((iv) => (
              <button key={iv} type="button" onClick={() => setInterval(iv)}
                className={cn('px-3 py-1.5 rounded-md text-sm border', interval === iv ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30' : 'border-zinc-200 dark:border-zinc-700')}>
                {iv === 'month' ? 'Monthly' : 'Yearly'}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {choices.map((p) => {
              const price = priceFor(p.id, interval);
              return (
                <label key={p.id} className={cn('flex items-center justify-between gap-3 px-3 py-2 rounded-md border cursor-pointer', packageId === p.id ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30' : 'border-zinc-200 dark:border-zinc-700')}>
                  <span className="flex items-center gap-2">
                    <input type="radio" name="upgrade-invoice-package" checked={packageId === p.id} onChange={() => setPackageId(p.id)} />
                    <span className="text-sm text-zinc-900 dark:text-zinc-100">{p.name}</span>
                  </span>
                  <span className="text-sm tabular-nums text-zinc-600 dark:text-zinc-300">{price ? taka(price.amount) : '—'}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}

const PAY_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success', pending: 'warning', failed: 'error', refunded: 'neutral', partial_refunded: 'neutral', disputed: 'error',
};
const PAY_METHOD: Record<string, string> = {
  sslcommerz: 'SSLCommerz', manual_cash: 'Cash', bank_transfer: 'Bank Transfer', bkash: 'bKash', nagad: 'Nagad', stripe: 'Stripe', paypal: 'PayPal',
};

const PAYMENTS_FILTERS: Array<{ v: 'paid' | 'all'; l: string }> = [
  { v: 'paid', l: 'Paid' },
  { v: 'all', l: 'All' },
];

function PaymentsTab({ payments }: { payments: AdminBusinessDetail['payments'] }) {
  const [filter, setFilter] = useState<'paid' | 'all'>('paid');
  if (payments.length === 0) {
    return <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No payments recorded for this business yet.</div>;
  }
  const shown = filter === 'paid' ? payments.filter((r) => r.status === 'paid') : payments;
  return (
    <div className="-mx-6">
      <div className="flex items-center gap-2 px-6 pb-3">
        {PAYMENTS_FILTERS.map((f) => (
          <button
            key={f.v}
            type="button"
            onClick={() => setFilter(f.v)}
            className={
              filter === f.v
                ? 'px-3 py-1 rounded-full text-xs font-medium bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                : 'px-3 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition'
            }
          >
            {f.l}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-6 py-3">Invoice</th>
              <th className="text-left font-medium px-4 py-3">Date</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-left font-medium px-4 py-3">Method</th>
              <th className="text-left font-medium px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {shown.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  No paid payments yet — switch to "All" to see pending/failed attempts.
                </td>
              </tr>
            ) : (
              shown.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200">{r.invoiceNo}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{r.paidAt ? formatDate(r.paidAt) : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Money minor={r.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{PAY_METHOD[r.method] ?? r.method}</td>
                  <td className="px-6 py-3">
                    <Badge tone={PAY_TONE[r.status] ?? 'neutral'}>{r.status.replace(/_/g, ' ')}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DatabaseTab({ db, dbMode }: { db: AdminBusinessDetail['database']; dbMode?: string }) {
  if (!db) {
    // "Grandfathered" is only true of an IMPORTED business (db_mode 'legacy'). A business
    // created on the shared platform yesterday is also dedicated-DB-less, and calling it
    // grandfathered legacy made a brand-new business read as an old import.
    const legacy = dbMode === 'legacy';
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Database" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          {legacy
            ? 'No dedicated database — this business was imported from a legacy deployment and runs on its shared database (grandfathered).'
            : 'No dedicated database — this business runs on the shared platform database, which is what the “Shared” option provisions.'}{' '}
          A dedicated DB is provisioned only for managed businesses, or registered for self-hosted (BYO-DB).
        </p>
      </Card>
    );
  }
  const tone = db.status === 'ready' ? 'success' : db.status === 'failed' ? 'error' : 'warning';
  return (
    <Card className="p-5 border-zinc-200 dark:border-zinc-700">
      <CardHeader
        title={db.mode === 'self_hosted' ? 'Self-hosted database' : 'Managed database'}
        subtitle={db.provisionedAt ? `Provisioned ${formatDate(db.provisionedAt)}` : 'Not provisioned yet'}
        action={<Badge tone={tone}>{db.status}</Badge>}
      />
      <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted (BYO)' : 'Managed'} />
        <Row k="Status" v={db.status} />
        <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
        <Row k="Database" v={<span className="mono text-xs">{db.dbName}</span>} />
        <Row k="Provisioned" v={db.provisionedAt ? formatDate(db.provisionedAt) : '—'} />
        <Row k="Attempts" v={String(db.attemptCount)} />
        {db.lastError && <Row k="Last error" v={<span className="text-red-600 dark:text-red-400 text-xs">{db.lastError}</span>} />}
      </dl>
    </Card>
  );
}

/* ── Extraction (move this business off shared/legacy MySQL onto its own dedicated
 *    database). Request-only from here — actual execution (preflight → freeze → copy
 *    → verify → cutover) is an operator-CLI action; see extraction-api.ts's module
 *    docstring for why a compromised admin token must not be able to trigger a live
 *    freeze by itself. ─────────────────────────────────────────────────────────── */
const EXTRACTABLE_DB_MODES: string[] = ['legacy', 'shared'];
const EXTRACTION_HAPPY_PATH: ExtractionState[] = ['requested', 'preflight', 'provisioning', 'frozen', 'copying', 'verifying', 'cutover', 'done'];
const EXTRACTION_STATE_LABEL: Record<ExtractionState, string> = {
  requested: 'Requested', preflight: 'Preflight', provisioning: 'Provisioning', frozen: 'Frozen (read-only)',
  copying: 'Copying data', verifying: 'Verifying', cutover: 'Cutover', done: 'Done',
  aborted: 'Aborted', rolled_back: 'Rolled back', declined: 'Declined',
};
const EXTRACTION_STATE_TONE: Record<ExtractionState, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  requested: 'info', preflight: 'info', provisioning: 'info', frozen: 'warning', copying: 'info',
  verifying: 'info', cutover: 'warning', done: 'success', aborted: 'error', rolled_back: 'error',
  declined: 'neutral',
};

/** Ordered 10-state stepper (spec §6 phases). `aborted`/`rolled_back`/`declined` fall off
 *  the happy path — a failure before cutover, a reversal after one, or the platform
 *  declining the request outright — so they render as a flag instead of a step
 *  position, which would misleadingly imply "further along". */
function ExtractionPhaseTimeline({ state }: { state: ExtractionState }) {
  if (state === 'aborted' || state === 'rolled_back' || state === 'declined') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0" />
        <span className="text-zinc-700 dark:text-zinc-200">
          {state === 'aborted'
            ? 'Aborted before cutover — the source database was never touched.'
            : state === 'declined'
              ? 'Declined by the platform — nothing was moved.'
              : 'Rolled back — reversed after cutover, back on the original database.'}
        </span>
      </div>
    );
  }
  const idx = EXTRACTION_HAPPY_PATH.indexOf(state);
  return (
    <ol className="flex flex-wrap items-center gap-y-2">
      {EXTRACTION_HAPPY_PATH.map((s, i) => (
        <li key={s} className="flex items-center">
          <span
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap',
              i === idx
                ? 'border-brand bg-brand-subtle text-brand dark:border-brand-dm dark:bg-brand-dm-subtle/30 dark:text-brand-dm'
                : i < idx
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                  : 'border-zinc-200 bg-zinc-50 text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-500',
            )}
          >
            {i < idx && <CheckCircle className="w-3 h-3" />}
            {EXTRACTION_STATE_LABEL[s]}
          </span>
          {i < EXTRACTION_HAPPY_PATH.length - 1 && (
            <span className="w-3 sm:w-4 h-px bg-zinc-200 dark:bg-zinc-700 mx-1 flex-shrink-0" />
          )}
        </li>
      ))}
    </ol>
  );
}

/** cutoverAt + 30 days, ISO — the informational grace-window date. Purely a display
 *  computation; nothing reads this to decide whether rollback is actually still possible
 *  (that stays an operator judgement call, per the copy on the banner below). */
function extractionGraceEndsAt(cutoverAt: string): string | null {
  const d = new Date(cutoverAt);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 30);
  return d.toISOString();
}

/**
 * States the SCHEDULE in terms a reader can act on, instead of reciting the stored date.
 *
 * This used to render one sentence unconditionally — "This extraction runs on the night of
 * 08 Aug 2026" — with no awareness that the date could already have gone. On 10 Aug it was
 * still confidently announcing a night two days in the past as though it were upcoming, which
 * is worse than saying nothing: it reads as "nothing is wrong, it is booked for the 8th" when
 * the truth is "it has missed two nights and is still waiting."
 *
 * A date that has passed while the row is still waiting is the ONLY interesting case here, and
 * it is the one the old copy handled worst. The queue is oldest-first and one extraction runs
 * at a time, so falling behind is normal and needs explaining rather than alarming.
 */
function ScheduledBanner({ scheduledFor }: { scheduledFor: string }) {
  const status = dateStatus(scheduledFor);
  const maintenance = 'The POS shows a maintenance page for a few minutes while it runs; nothing else changes for the business.';
  // "One at a time, oldest first" was the original wording here and it MISREAD as "one per
  // night" — the very limit that was removed on 2026-08-09. Say the throughput out loud:
  // several run in the same night, back-to-back, and only simultaneity is forbidden.
  const queueRule = `Several extractions can run in the same night: they start at ${EXTRACTION_SLOT_TIME} and run back-to-back, about 5–10 minutes each, never two at once. Oldest request goes first, so this may begin somewhat later than ${EXTRACTION_SLOT_TIME} if others are ahead of it.`;

  if (status === 'past') {
    return (
      <Banner tone="warning" title="Overdue — still waiting to run">
        This was scheduled for <strong>{slotOn(scheduledFor)}</strong>, which has passed, and it
        has not run yet. It is still queued and will start on the next nightly run —{' '}
        <strong>tonight, {slotOn(dhakaToday())}</strong>. {queueRule} If any extraction fails, the
        rest of that night is stood down deliberately, which is the usual reason one is late.
        Nothing has been done to this business in the meantime: it has not been frozen and its
        data has not been touched. {maintenance}
      </Banner>
    );
  }

  if (status === 'today') {
    return (
      <Banner tone="info" title="Runs tonight">
        This runs <strong>tonight, {slotOn(scheduledFor)}</strong>. {queueRule} {maintenance}
      </Banner>
    );
  }

  return (
    <Banner tone="info" title="Scheduled">
      This runs on the night of <strong>{slotOn(scheduledFor)}</strong>. {queueRule}{' '}
      {maintenance}
    </Banner>
  );
}

function ExtractionTab({ businessId, businessName, dbMode }: { businessId: string; businessName: string; dbMode?: string }) {
  const { data, isLoading, isError, refetch } = useBusinessExtraction(businessId);
  const [requestOpen, setRequestOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const qc = useQueryClient();

  async function approve() {
    if (!data?.current || approving) return;
    setApproving(true);
    setApproveError(null);
    try {
      await approveBusinessExtraction(data.current.publicId, scheduleDate);
      void qc.invalidateQueries({ queryKey: ['admin', 'business', businessId, 'extraction'] });
    } catch (e) {
      // Same class as setStatus above — a failed approve must not look like a no-op.
      setApproveError(e instanceof Error ? e.message : 'Could not approve the extraction. Please try again.');
    } finally {
      setApproving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="skeleton h-6 w-40 rounded" />
        <div className="skeleton h-24 rounded-lg" />
      </div>
    );
  }

  // A failed fetch must NOT fall through to "no extraction requested yet" — this tab
  // gates a destructive freeze, and an operator misreading "nothing pending" while the
  // status call actually failed is exactly the wrong kind of silent failure here.
  if (isError) {
    return (
      <Banner tone="error" title="Couldn't load extraction status" action={<Button variant="secondary" size="sm" onClick={() => void refetch()}>Retry</Button>}>
        The request to the API failed — this is NOT the same as "no extraction requested". Retry, or check the API.
      </Banner>
    );
  }

  // `current` is the requested-or-active row (per the GET contract); once an attempt
  // reaches a terminal state (done/aborted/rolled_back) it drops out of `current`, so
  // fall back to the newest history row to keep showing what actually happened.
  const latest = data?.current ?? data?.history[0] ?? null;
  const extractable = EXTRACTABLE_DB_MODES.includes(dbMode ?? '');
  const graceEndsAt = latest && latest.state === 'done' && latest.cutoverAt ? extractionGraceEndsAt(latest.cutoverAt) : null;

  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader
          title="Move to a dedicated database"
          subtitle="Copies this business's data out of the shared/legacy database into one it alone owns. A short off-hours freeze happens during the move; the POS shows a maintenance page for that window."
          action={
            !data?.current ? (
              extractable ? (
                <Button size="sm" iconLeft={<Database className="w-4 h-4" />} onClick={() => setRequestOpen(true)}>
                  Request extraction
                </Button>
              ) : (
                // Was a disabled button explained only by a `title` tooltip — invisible on
                // touch. State the rule, and say what this business already is, so the
                // reader does not have to work out which half of the rule they failed.
                <span className="text-xs text-zinc-500 dark:text-zinc-400 max-w-xs text-right">
                  Already on its own database ({dbMode ?? 'unknown'} mode) — only legacy or shared
                  businesses can be extracted.
                </span>
              )
            ) : !data.current.approvedAt ? (
              <div className="flex items-end gap-2 flex-wrap">
                <Input
                  type="date"
                  label="Run on the night of"
                  value={scheduleDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
                <Button size="sm" disabled={!scheduleDate || approving} loading={approving} onClick={() => void approve()}>
                  Approve &amp; schedule
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setDeclineOpen(true)}>
                  Decline
                </Button>
              </div>
            ) : undefined
          }
        />
        {approveError && <p className="mb-4 text-sm text-rose-600 dark:text-rose-400">{approveError}</p>}
        {!latest ? (
          <p className="text-sm text-zinc-600 dark:text-zinc-300">No extraction has been requested for this business yet.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={EXTRACTION_STATE_TONE[latest.state]}>{EXTRACTION_STATE_LABEL[latest.state]}</Badge>
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mono">{latest.publicId}</span>
            </div>
            <ExtractionPhaseTimeline state={latest.state} />
            <dl className="grid sm:grid-cols-3 gap-y-2.5 gap-x-6 text-sm pt-2">
              <Row k="Requested" v={latest.requestedAt ? formatDate(latest.requestedAt) : '—'} />
              <Row k="Frozen" v={latest.frozenAt ? formatDate(latest.frozenAt) : '—'} />
              <Row k="Cutover" v={latest.cutoverAt ? formatDate(latest.cutoverAt) : '—'} />
            </dl>
            {latest.error && <p className="text-sm text-rose-600 dark:text-rose-400">{latest.error}</p>}
          </div>
        )}
      </Card>

      {data?.current?.approvedAt && data.current.scheduledFor && (
        <ScheduledBanner scheduledFor={data.current.scheduledFor} />
      )}

      {graceEndsAt && latest?.cutoverAt && (
        <Banner tone="info" title="30-day rollback window">
          This business finished moving to its dedicated database on {formatDate(latest.cutoverAt)}. The old
          database is kept until <strong>{formatDate(graceEndsAt)}</strong>. Rollback after this date is a
          data-loss decision, and nothing is ever deleted automatically.
        </Banner>
      )}

      {data && data.history.length > 1 && (
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Extraction history" subtitle="Every attempt for this business, newest first." />
          <div className="overflow-x-auto nice-scroll -mx-5">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                <tr>
                  <th className="text-left font-medium px-5 py-2">State</th>
                  <th className="text-left font-medium px-4 py-2">Requested</th>
                  <th className="text-left font-medium px-4 py-2">Frozen</th>
                  <th className="text-left font-medium px-4 py-2">Cutover</th>
                  <th className="text-left font-medium px-5 py-2">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {data.history.map((h) => (
                  <tr key={h.publicId}>
                    <td className="px-5 py-2.5"><Badge tone={EXTRACTION_STATE_TONE[h.state]}>{EXTRACTION_STATE_LABEL[h.state]}</Badge></td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.requestedAt ? formatDate(h.requestedAt) : '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.frozenAt ? formatDate(h.frozenAt) : '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.cutoverAt ? formatDate(h.cutoverAt) : '—'}</td>
                    <td className="px-5 py-2.5 text-rose-600 dark:text-rose-400 text-xs">{h.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ExtractionRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} businessId={businessId} businessName={businessName} />
      {data?.current && (
        <ExtractionDeclineModal
          open={declineOpen}
          onClose={() => setDeclineOpen(false)}
          publicId={data.current.publicId}
          businessId={businessId}
        />
      )}
    </div>
  );
}

/**
 * Typed-confirmation request modal — same pattern as CustomerDetail.tsx's
 * CancelSubscriptionControl (destructive button disabled until the input equals the
 * required word). There is deliberately no execute button anywhere in this modal:
 * requesting only creates the 'requested' row, and running the move (preflight → … →
 * cutover) stays an operator-CLI action — see extraction-api.ts's module docstring for
 * why a compromised admin token must not be able to trigger a live freeze by itself.
 */
function ExtractionRequestModal({ open, onClose, businessId, businessName }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
}) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (busy) return;
    setConfirmText('');
    setError(null);
    onClose();
  }
  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await requestBusinessExtraction(businessId, { acknowledgement: confirmText });
      void qc.invalidateQueries({ queryKey: ['admin', 'business', businessId, 'extraction'] });
      setConfirmText('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not request the extraction. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Request extraction to a dedicated database?"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={confirmText !== 'EXTRACT' || busy} loading={busy}>
            Request extraction
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          This moves <strong className="text-zinc-900 dark:text-zinc-100">{businessName}</strong> off the shared/legacy
          database and onto one it alone owns. During the move:
        </p>
        <ul className="list-disc list-inside text-sm text-zinc-600 dark:text-zinc-300 space-y-1">
          <li>The business is frozen read-only for a short, off-hours window.</li>
          <li>Its POS shows a maintenance page for that window — no sales or edits go through.</li>
          <li>The old database is kept for a 30-day rollback window after cutover.</li>
          <li>Nothing is ever deleted automatically — rollback stays a deliberate, manual action.</li>
        </ul>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          This only records the request. Running the move itself is a separate operator action — there is no
          "execute" button here or anywhere in the portal.
        </p>
        <Input label="Type EXTRACT to confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="EXTRACT" />
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Decline a pending extraction. Terminal — the request is closed and the business is
 *  free to request again later; the reason is stored on the row (shown as its `error`
 *  in history, same as every other terminal state). Nothing is moved or deleted. */
function ExtractionDeclineModal({ open, onClose, publicId, businessId }: {
  open: boolean;
  onClose: () => void;
  publicId: string;
  businessId: string;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (submitting) return;
    setReason('');
    setError(null);
    onClose();
  }
  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await declineBusinessExtraction(publicId, reason.trim());
      void qc.invalidateQueries({ queryKey: ['admin', 'business', businessId, 'extraction'] });
      setReason('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not decline the request.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Decline this extraction request?"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={!reason.trim() || submitting} loading={submitting}>
            Decline request
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          The request is closed and the business becomes free to request again later. Nothing is
          moved and nothing is deleted.
        </p>
        <label className="block text-sm">
          <span className="block mb-1.5 font-medium text-zinc-900 dark:text-zinc-100">Reason (required)</span>
          <textarea
            className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** The business's POS locations (branches/counters), read from the Postgres
 *  mirror the 15-min POS→Postgres cron refreshes — the same locations the POS
 *  "Select location" dropdown shows, without a live Laravel query. */
function LocationsTab({ locations }: { locations: AdminBusinessDetail['locations'] }) {
  if (locations.length === 0) {
    return (
      <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">
        No locations mirrored for this business yet. The POS mirror refreshes every
        15 minutes — a business that has never synced (or has no POS link) shows nothing here.
      </div>
    );
  }
  const synced = locations[0]?.mirroredAt;
  return (
    <div className="space-y-3">
      {locations.map((l) => (
        <Card key={l.id} className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500"><MapPin className="w-4 h-4" /></span>
              <div>
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{l.name}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                  POS location #{l.laravelLocationId ?? '—'}
                </div>
              </div>
            </div>
            <Badge tone={l.isActive ? 'success' : 'neutral'}>{l.isActive ? 'Active' : 'Inactive'}</Badge>
          </div>
        </Card>
      ))}
      {synced && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          Synced from the POS {timeAgo(synced)} · refreshes every 15 minutes.
        </p>
      )}
    </div>
  );
}

function DomainsTab({ domains }: { domains: AdminBusinessDetail['domains'] }) {
  if (domains.length === 0) {
    return (
      <div className="space-y-3">
        <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">
          No custom domains for this business — it uses the platform / reseller domain.
        </div>
        <Link to="/admin/domains">
          <Button variant="secondary" size="sm">Manage domains</Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {domains.map((d) => (
        <Card key={d.host} className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500"><Globe className="w-4 h-4" /></span>
              <div>
                <div className="font-medium text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
                  {d.host}
                  {d.isPrimary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">DNS {d.dnsStatus} · TLS {d.tlsStatus}</div>
              </div>
            </div>
            <Badge tone={d.dnsStatus === 'verified' ? 'success' : d.dnsStatus === 'failed' ? 'error' : 'warning'}>
              {d.dnsStatus === 'verified' ? 'Verified' : d.dnsStatus === 'failed' ? 'Failed' : 'Pending'}
            </Badge>
          </div>
        </Card>
      ))}
      <Link to="/admin/domains">
        <Button variant="secondary" size="sm">Manage domains</Button>
      </Link>
    </div>
  );
}

function UsersTab({ businessId, onChanged }: { businessId: string; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-3">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">Owners and staff with POS access to this business. POS roles are read live from its POS.</p>
        <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setOpen(true)}>Add user</Button>
      </div>
      <BusinessUsersTable businessId={businessId} canResetPassword onChanged={onChanged} />
      <AddBusinessUserModal open={open} onClose={() => setOpen(false)} businessId={businessId} onChanged={onChanged} />
    </div>
  );
}

/** Edit the business's SaaS-side info (display name + owner contact email). */
function EditBusinessModal({ open, onClose, businessId, initialName, initialOwnerEmail, onChanged }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  initialName: string;
  initialOwnerEmail: string;
  onChanged: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [ownerEmail, setOwnerEmail] = useState(initialOwnerEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-seed when reopened for fresh values.
  const [wasOpen, setWasOpen] = useState(false);
  if (open && !wasOpen) {
    setName(initialName);
    setOwnerEmail(initialOwnerEmail);
    setError(null);
    setWasOpen(true);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }
  async function save() {
    if (!name.trim()) { setError('Business name cannot be empty.'); return; }
    setBusy(true); setError(null);
    try {
      await updateAdminBusinessInfo(businessId, { name: name.trim(), ownerEmail: ownerEmail.trim() || null });
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit business"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Business name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Owner email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} hint="Billing + renewal emails go here." />
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          This changes the SaaS-side record. The name shown inside the POS itself is a POS business setting.
        </p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Admin: add a user to THIS business — Create (set a password now) or Invite (email a link). The
 *  chosen POS role maps to the Laravel role (Admin#<lbid>/Cashier#<lbid>) when pushed to the POS DB. */
function AddBusinessUserModal({ open, onClose, businessId, onChanged }: { open: boolean; onClose: () => void; businessId: string; onChanged: () => void }) {
  const [action, setAction] = useState<'create' | 'invite'>('create');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [posRole, setPosRole] = useState<string>('Cashier');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | CreateUserResult | null>(null);

  function close() {
    setAction('create'); setEmail(''); setName(''); setUsername(''); setPosRole('Cashier'); setPassword(''); setBusy(false); setError(null); setResult(null);
    onClose();
  }
  async function submit() {
    if (!email.trim() || !businessId) { setError('A valid email is required.'); return; }
    if (action === 'create' && password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (action === 'create' && username.trim() && !/^[a-z0-9][a-z0-9._-]{2,39}$/i.test(username.trim())) {
      setError('Username must be 3–40 characters: letters, digits, dot, dash or underscore.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await createAdminBusinessUser(businessId, {
        action, email: email.trim(), name: name.trim() || undefined, posRole,
        password: action === 'create' ? password : undefined,
        username: action === 'create' ? username.trim() || undefined : undefined,
      });
      setResult(res);
      onChanged();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const message = e instanceof Error && e.message ? e.message : null;
      setError(status === 409 ? (message ?? 'That person already has access to this business.') : status === 403 ? 'You do not have permission to add users here.' : message ?? (action === 'create' ? 'Could not create the account.' : 'Could not send the invite.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a user to this business'}
      size="md"
      footer={result ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={!email.trim() || busy} loading={busy}>{action === 'create' ? 'Create account' : 'Send invite'}</Button>
        </>
      )}
    >
      {result ? (
        'inviteUrl' in result ? <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} /> : <AccountCreatedPanel email={result.email} />
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input label="Email" required type="email" placeholder="teammate@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Full name" placeholder="(optional)" value={name} onChange={(e) => setName(e.target.value)} />
          {action === 'create' && (
            <>
              <Input label="Username" placeholder="(optional — auto-generated if empty)" hint="Their POS + portal login username." value={username} onChange={(e) => setUsername(e.target.value)} />
              <Input label="Password" required type="password" hint="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
            </>
          )}
          <PosRoleSelector businessId={businessId} value={posRole} onChange={setPosRole} />
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

const AUDIT_DIFF_FIELD_LABEL: Record<string, string> = {
  package: 'Package',
  interval: 'Interval',
  priceMinor: 'Price',
};

function humanizeAuditAction(action: string): string {
  const s = action.replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Event';
}

/** Friendlier titles for actions whose raw slug reads poorly to a non-technical admin. */
const AUDIT_ACTION_TITLE: Record<string, string> = {
  'pos.impersonation.start': 'Support access — opened this business’s POS',
  'business.plan_changed': 'Subscription plan changed',
  'business.plan_change_invoiced': 'Upgrade invoice issued (plan not changed yet)',
  'invoice.voided': 'Open invoice voided',
  'business.addon_attached': 'Add-on attached (invoice issued)',
  'business.addon_detached': 'Add-on removed',
  'business.subscription_catalog_linked': 'Subscription linked to the catalog',
  'business.subscription_trial_restored': 'Trial state restored',
  'business.info_updated': 'Business details edited',
};

/** One-sentence plain-language explanation rendered under known audit events, so the
 *  tab reads like a story instead of system slugs (what does "pos impersonation" mean?). */
const AUDIT_ACTION_EXPLAIN: Record<string, string> = {
  'pos.impersonation.start':
    'A platform admin opened this business’s POS signed in as the owner, in READ-ONLY support mode — they could look around to help, but could not change or sell anything.',
  'business.plan_changed':
    'The subscription was re-pointed at a package/price. If both sides below look identical, the change was between two same-named packages in different catalogs, or a re-link — see the reason line.',
  'business.plan_change_invoiced':
    'An admin issued an upgrade invoice on the customer’s behalf. The plan only changes once that invoice is fully paid.',
  'invoice.voided':
    'An unpaid invoice was cancelled. The next billing cycle re-issues a fresh one at the then-current plan price.',
  'business.addon_attached':
    'The business took an add-on: the extra capacity switched on immediately and an invoice for it was issued.',
  'business.subscription_catalog_linked':
    'A legacy (pre-SaaS) subscription was linked to the current catalog so it renews and invoices automatically — the customer’s price did not change.',
  'business.subscription_trial_restored':
    'An earlier suspend/reactivate had flipped this unpaid trial to "active" by mistake; the trial label was put back.',
};

function humanizeAuditKey(key: string): string {
  const s = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

function formatAuditDiffValue(field: string, v: unknown): string {
  if (field === 'priceMinor' && typeof v === 'number') return '৳' + (v / 100).toLocaleString('en-US');
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/** "7:17 PM" — always 12-hour clock with AM/PM (never bare 24h "19:17"). */
function formatAuditTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function isFromToShape(v: unknown): v is { from: unknown; to: unknown } {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'from' in v && 'to' in v;
}

function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** The most recent business.plan_changed entry, if any — audit[] is already occurredAt-desc.
 *  Prefers the diff shape (entries written after this feature shipped); falls back to the
 *  older flat metadata shape (fromPackage/toPackage) so entries from before this deploy
 *  still render real package names instead of "— → —". */
function findLastPlanChange(audit: AdminBusinessDetail['audit']) {
  const entry = audit.find((a) => a.action === 'business.plan_changed');
  if (!entry) return null;
  const diff = entry.diff as Record<string, { from: unknown; to: unknown }> | null;
  const pkg = diff?.package;
  const meta = entry.metadata as { fromPackage?: unknown; toPackage?: unknown } | null;
  return {
    occurredAt: entry.occurredAt,
    actorEmail: entry.actorEmail,
    fromPackage: pkg ? String(pkg.from ?? '—') : meta?.fromPackage ? String(meta.fromPackage) : '—',
    toPackage: pkg ? String(pkg.to ?? '—') : meta?.toPackage ? String(meta.toPackage) : '—',
  };
}

function AuditTab({ audit }: { audit: AdminBusinessDetail['audit'] }) {
  if (audit.length === 0) {
    return <div className="px-2 py-6 text-sm text-zinc-500 dark:text-zinc-400">No audit events recorded for this business yet.</div>;
  }
  return (
    <ul className="space-y-3">
      {audit.map((a) => {
        const flagged = a.outcome === 'failure' || a.severity === 'warning' || a.severity === 'critical';
        const diffEntries = a.diff ? Object.entries(a.diff) : [];
        const metaEntries = !a.diff && a.metadata ? Object.entries(a.metadata) : [];
        return (
          <li key={a.id} className="flex items-start gap-3 text-sm">
            <span
              className={
                flagged
                  ? 'w-7 h-7 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5'
                  : 'w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 flex-shrink-0 mt-0.5'
              }
            >
              {flagged ? <AlertTriangle className="w-3.5 h-3.5" /> : <Database className="w-3.5 h-3.5" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-zinc-900 dark:text-zinc-100">
                <span className="font-medium">{AUDIT_ACTION_TITLE[a.action] ?? humanizeAuditAction(a.action)}</span>
                <span className="text-zinc-500 dark:text-zinc-400"> · {a.category}</span>
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                {a.actorEmail}{a.actorRole ? ` (${a.actorRole.replace(/_/g, ' ')})` : ''} ·{' '}
                {a.occurredAt ? `${formatDate(a.occurredAt)}, ${formatAuditTime(a.occurredAt)}` : '—'} ·{' '}
                {a.occurredAt ? timeAgo(a.occurredAt) : ''}
              </div>
              {AUDIT_ACTION_EXPLAIN[a.action] && (
                <p className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400 italic">
                  {AUDIT_ACTION_EXPLAIN[a.action]}
                </p>
              )}
              {typeof a.metadata?.reason === 'string' && a.metadata.reason.trim() !== '' && (
                <div className="mt-1.5 text-xs rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5 text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">Reason: </span>{String(a.metadata.reason)}
                </div>
              )}
              {diffEntries.length > 0 && (
                <div className="mt-2 rounded-md border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                  {diffEntries.map(([k, v], i) => (
                    <div
                      key={k}
                      className={
                        i > 0
                          ? 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs border-t border-zinc-200 dark:border-zinc-700'
                          : 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs'
                      }
                    >
                      <span className="font-medium text-zinc-600 dark:text-zinc-300">{AUDIT_DIFF_FIELD_LABEL[k] ?? humanizeAuditKey(k)}</span>
                      <span className="text-zinc-500 dark:text-zinc-400 text-right">{formatAuditDiffValue(k, v.from)}</span>
                      <span className="text-zinc-400">→</span>
                      <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                        {formatAuditDiffValue(k, v.to)}
                        {formatAuditDiffValue(k, v.from) === formatAuditDiffValue(k, v.to) && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">(unchanged)</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {metaEntries.length > 0 && (
                <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
                  {metaEntries.map(([k, v]) => (
                    <div key={k}>
                      {humanizeAuditKey(k)}:{' '}
                      {isFromToShape(v) ? (
                        <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v.from)} → {formatMetaValue(v.to)}</span>
                      ) : (
                        <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-zinc-500 dark:text-zinc-400 w-28 flex-shrink-0">{k}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100 flex-1 min-w-0">{v}</dd>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mt-0.5 tabular-nums">{v}</div>
    </div>
  );
}
